import React, { useState, useEffect } from 'react'
import { useHistory, useParams } from 'react-router-dom'

import {
  Button,
  ButtonToolbar,
  Form,
  Input,
  InputNumber,
  Panel,
  Notification,
  toaster
} from 'rsuite'

import SettingsTopMenu from '@components/settings/SettingsTopMenu'
import GraphQLCommonErrorDisplay from '@components/common/GraphQLCommonErrorDisplay'
import AppLoader from '@components/common/AppLoader'

import { ROUTE_SETTINGS_TOPOLOGY } from '@@partner:config/routes'

import { useFetchTopology } from '@@partner:graphql/query/topology.gql'
import { useSaveCountry, CountryInput } from '@@partner:graphql/mutation/topology/saveCountry.gql'

interface CountryFormPageParams {
  countryId?: string
}

const DEFAULT_FORM_DATA: CountryInput = {
  name: '',
  ISO2: '',
  ISO3: '',
  web_domain: '',
  iso_numeric: undefined,
  phone_code: undefined,
  currency_name: '',
  currency_symbol: '',
  language_codes: ''
}

export default function CountryFormPage () {
  const history = useHistory()
  const { countryId } = useParams<CountryFormPageParams>()
  const isEditing = !!countryId

  const { data: topologyData, loading: topologyLoading } = useFetchTopology()
  const [ saveCountry, { loading: saving } ] = useSaveCountry()

  const [ formData, setFormData ] = useState<CountryInput>(DEFAULT_FORM_DATA)

  useEffect(() => {
    if (isEditing && topologyData?.topology?.countries) {
      const country = topologyData.topology.countries.find(c => c.id === countryId)
      if (country) {
        setFormData({
          id: country.id,
          name: country.name,
          ISO2: country.ISO2,
          ISO3: country.ISO3,
          web_domain: country.web_domain ?? '',
          iso_numeric: country.iso_numeric ?? undefined,
          phone_code: country.phone_code ?? undefined,
          currency_name: country.currency_name ?? '',
          currency_symbol: country.currency_symbol ?? '',
          language_codes: country.language_codes ?? ''
        })
      }
    }
  }, [ isEditing, countryId, topologyData ])

  const handleSubmit = async () => {
    if (!formData.name || !formData.ISO2 || !formData.ISO3) {
      toaster.push(
        <Notification type="error" header="Ошибка">Заполните обязательные поля (Название, ISO2, ISO3)</Notification>,
        { placement: 'topEnd' }
      )
      return
    }

    try {
      await saveCountry({
        variables: {
          countryInput: formData,
          upsert: !isEditing
        }
      })

      toaster.push(
        <Notification type="success" header="Успех">
          Страна {isEditing ? 'обновлена' : 'создана'}
        </Notification>,
        { placement: 'topEnd' }
      )

      history.push(ROUTE_SETTINGS_TOPOLOGY)
    }
    catch (err: any) {
      toaster.push(
        <Notification type="error" header="Ошибка">{err.message}</Notification>,
        { placement: 'topEnd' }
      )
    }
  }

  if (topologyLoading && isEditing) {
    return <AppLoader />
  }

  return (
    <div className="CountryFormPage">
      <h1 className="u-heading">{isEditing ? 'Редактирование страны' : 'Создание страны'}</h1>
      <SettingsTopMenu />

      <Panel shaded className="mt-4">
        <Form fluid>
          <Form.Group>
            <Form.ControlLabel>Название *</Form.ControlLabel>
            <Input
              value={formData.name}
              onChange={(value) => setFormData({ ...formData, name: value })}
              placeholder="Казахстан"
            />
          </Form.Group>

          <Form.Group>
            <Form.ControlLabel>ISO2 код *</Form.ControlLabel>
            <Input
              value={formData.ISO2}
              onChange={(value) => setFormData({ ...formData, ISO2: value.toUpperCase() })}
              placeholder="KZ"
              maxLength={2}
            />
            <Form.HelpText>Двухбуквенный код страны (ISO 3166-1 alpha-2)</Form.HelpText>
          </Form.Group>

          <Form.Group>
            <Form.ControlLabel>ISO3 код *</Form.ControlLabel>
            <Input
              value={formData.ISO3}
              onChange={(value) => setFormData({ ...formData, ISO3: value.toUpperCase() })}
              placeholder="KAZ"
              maxLength={3}
            />
            <Form.HelpText>Трёхбуквенный код страны (ISO 3166-1 alpha-3)</Form.HelpText>
          </Form.Group>

          <Form.Group>
            <Form.ControlLabel>Телефонный код</Form.ControlLabel>
            <InputNumber
              value={formData.phone_code}
              onChange={(value) => setFormData({ ...formData, phone_code: value as number })}
              placeholder="7"
            />
          </Form.Group>

          <Form.Group>
            <Form.ControlLabel>Веб-домен</Form.ControlLabel>
            <Input
              value={formData.web_domain ?? ''}
              onChange={(value) => setFormData({ ...formData, web_domain: value })}
              placeholder=".kz"
            />
          </Form.Group>

          <Form.Group>
            <Form.ControlLabel>Валюта (название)</Form.ControlLabel>
            <Input
              value={formData.currency_name ?? ''}
              onChange={(value) => setFormData({ ...formData, currency_name: value })}
              placeholder="Тенге"
            />
          </Form.Group>

          <Form.Group>
            <Form.ControlLabel>Валюта (символ)</Form.ControlLabel>
            <Input
              value={formData.currency_symbol ?? ''}
              onChange={(value) => setFormData({ ...formData, currency_symbol: value })}
              placeholder="₸"
            />
          </Form.Group>

          <Form.Group>
            <Form.ControlLabel>Коды языков</Form.ControlLabel>
            <Input
              value={formData.language_codes ?? ''}
              onChange={(value) => setFormData({ ...formData, language_codes: value })}
              placeholder="kk,ru"
            />
            <Form.HelpText>Коды языков через запятую</Form.HelpText>
          </Form.Group>

          <ButtonToolbar className="mt-6">
            <Button
              appearance="primary"
              onClick={handleSubmit}
              loading={saving}
            >
              {isEditing ? 'Сохранить' : 'Создать'}
            </Button>
            <Button
              appearance="subtle"
              onClick={() => history.push(ROUTE_SETTINGS_TOPOLOGY)}
            >
              Отмена
            </Button>
          </ButtonToolbar>
        </Form>
      </Panel>
    </div>
  )
}
